import { useEffect, useRef, useState } from "react";
import "./CustomFeatureModal.scss";
import getDataFromApi from "../../../../components/getDataFromApi";

const extractApiErrorMessage = (error) => {
  const candidates = [
    error?.body?.message,
    error?.body?.detail,
    error?.response?.data?.message,
    error?.response?.data?.detail,
    error?.detail,
    error?.message,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "object") {
      const nestedMessage = candidate.message || candidate.detail;
      if (nestedMessage) return String(nestedMessage);
      continue;
    }

    const rawMessage = String(candidate).replace(/^\d{3}:\s*/, "");
    const jsonStart = rawMessage.indexOf("{");

    if (jsonStart !== -1) {
      try {
        const parsed = JSON.parse(rawMessage.substring(jsonStart));
        const parsedMessage = parsed?.message || parsed?.detail;
        if (parsedMessage) return String(parsedMessage);
      } catch { }
    }

    if (rawMessage.trim()) return rawMessage.trim();
  }

  return "";
};

const isPlanAssignmentError = (message) => {
  const lowerMessage = String(message || "").toLowerCase();

  return (
    lowerMessage.includes("is used in") ||
    lowerMessage.includes("assigned to") ||
    lowerMessage.includes("used in plan") ||
    lowerMessage.includes("used by plan") ||
    lowerMessage.includes("active plan") ||
    lowerMessage.includes("locked because") ||
    lowerMessage.includes("modifications are disabled")
  );
};

const getPlanAssignmentEditMessage = (itemType) =>
  `This ${itemType} is assigned to one or more active plans or templates. Remove it from those plans or templates before editing it.`;

const throwIfPlanAssignmentResponse = (response) => {
  const apiMessage = extractApiErrorMessage(response);

  if (!isPlanAssignmentError(apiMessage)) return;

  const error = new Error(apiMessage);
  error.type = "EDIT_BLOCKED";
  throw error;
};

const normalizeDependencyValues = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter((val) => val !== null && val !== undefined && String(val).trim() !== "")
      .map((val) => {
        if (typeof val === "object") {
          return String(val.id ?? val._id ?? val.value ?? val.name ?? "");
        }

        return String(val);
      })
      .filter((val) => val.trim() !== "");
  }

  if (value === null || value === undefined) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        return normalizeDependencyValues(JSON.parse(trimmed));
      } catch { }
    }

    return trimmed
      .split(",")
      .map((val) => val.trim())
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const objectValue = value.id ?? value._id ?? value.value ?? value.name ?? "";
    return objectValue === "" ? [] : [String(objectValue)];
  }

  return [String(value)];
};

const getEditDependencyValues = (editData, schemaKey, apiKeys) => {
  const keys = [schemaKey, ...apiKeys];
  const sources = [editData?.raw?.metadata, editData?.raw, editData];

  for (const source of sources) {
    if (!source) continue;

    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined) {
        const values = normalizeDependencyValues(value);
        if (values.length) return values;
      }
    }
  }

  return [];
};

const mergeDependencyItems = (schemaItems, fallbackOptions = []) => {
  const enumValues = [...(schemaItems?.enum || [])];
  const enumNames = [...(schemaItems?.enumNames || [])];
  const seen = new Set(enumValues.map((value) => String(value)));

  fallbackOptions.forEach((option) => {
    const value =
      option?.id ??
      option?._id ??
      option?.value ??
      option?.raw?._id ??
      "";

    if (value === "" || value === null || value === undefined) return;

    const key = String(value);
    if (seen.has(key)) {
      const existingIndex = enumValues.findIndex((item) => String(item) === key);
      const fallbackName = option?.name || option?.label || option?.raw?.name || "";
      if (
        existingIndex >= 0 &&
        fallbackName &&
        (!enumNames[existingIndex] || String(enumNames[existingIndex]) === key)
      ) {
        enumNames[existingIndex] = fallbackName;
      }
      return;
    }

    enumValues.push(key);
    enumNames.push(option?.name || option?.label || option?.raw?.name || key);
    seen.add(key);
  });

  return { enum: enumValues, enumNames };
};

export default function CustomFeatureModal({
  showModal,
  onClose,
  onSubmit,
  entitlementId,
  editData,
  isEdit,
  featureOptions = [],
  resourceOptions = [],
  moduleOptions = [],
  entitlementName,
}) {
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [editBlocked, setEditBlocked] = useState(false);
  const [errors, setErrors] = useState({});

  const lastEntitlementRef = useRef(null);

  useEffect(() => {
    if (!showModal) return;

    const entitlementChanged =
      lastEntitlementRef.current !== null &&
      String(lastEntitlementRef.current) !== String(entitlementId);

    if (schema && !entitlementChanged) {
      return;
    }

    setFormLoading(true);

    getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_feature`, "get")
      .then((result) => {
        const jsonSchema = result.json_schema;
        setSchema(jsonSchema);
        lastEntitlementRef.current = entitlementId;
        setFormLoading(false);
      })
      .catch((e) => {
        console.log(e.message);
        setFormLoading(false);
      });
  }, [showModal, entitlementId, schema]);

  useEffect(() => {
    if (!showModal || !schema) return;

    const defaults = {};

    const hasResourceDependencyField =
      Boolean(schema?.properties?.["Resource Dependencies"]) ||
      resourceOptions.length > 0;

    Object.entries(schema.properties).forEach(([key, val]) => {
      if (key === "Feature Category") {
        const matched = (val.enum || []).find(
          (x) =>
            String(x).toLowerCase() ===
            String(entitlementId || "").toLowerCase()
        );

        defaults[key] = matched || val.enum?.[val.enum.length - 1] || "";
      } else if (key === "Feature Dependencies" || key === "Resource Dependencies") {
        defaults[key] = [];
      } else if (val.default !== undefined) {
        defaults[key] = val.default;
      } else if (val.type === "boolean") {
        defaults[key] = false;
      } else if (key === "Price") {
        defaults[key] = 0;
      } else {
        defaults[key] = "";
      }
    });

    if (hasResourceDependencyField && !Object.prototype.hasOwnProperty.call(defaults, "Resource Dependencies")) {
      defaults["Resource Dependencies"] = [];
    }

    if (isEdit && editData) {
      defaults["Feature Name"] =
        editData.raw?.name || editData.name || "";

      defaults["Feature Description"] =
        editData.raw?.description ||
        editData.raw?.feature_description ||
        editData.description ||
        "";

      defaults["Price"] =
        editData.raw?.base_price ??
        editData.base_price ??
        editData.price ??
        0;

      defaults["Core Feature"] =
        editData.raw?.is_core ??
        editData.locked ??
        false;

      defaults["Feature Dependencies"] = getEditDependencyValues(
        editData,
        "Feature Dependencies",
        ["dependencies", "feature_dependencies", "features_dependencies"]
      );

      if (hasResourceDependencyField) {
        defaults["Resource Dependencies"] = getEditDependencyValues(
          editData,
          "Resource Dependencies",
          ["resource_dependencies", "resources_dependencies"]
        );
      }

      const enumValues = schema?.properties?.["Feature Category"]?.enum || [];

      const editCategory =
        editData.raw?.category ||
        editData.category ||
        entitlementId ||
        "";

      const matchedCategory = enumValues.find(
        (x) => String(x).toLowerCase() === String(editCategory).toLowerCase()
      );

      defaults["Feature Category"] =
        matchedCategory || defaults["Feature Category"];
    }

    setFormData(defaults);
    setInitialFormData(JSON.parse(JSON.stringify(defaults)));
    setErrors({});
    setSubmitError(null);
    setEditBlocked(false);
  }, [showModal, schema, entitlementId, isEdit, editData, resourceOptions.length]);

  const update = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const clearFieldError = (key) => {
    setErrors((prev) => ({ ...prev, [key]: "" }));
    if (!editBlocked) {
      setSubmitError(null);
    }
  };

  const resolveCategory = () => {
    const current = formData["Feature Category"];
    if (!schema || !entitlementId) return current;

    const enums = schema?.properties?.["Feature Category"]?.enum || [];

    if (enums.includes(current)) return current;

    const found = enums.find(
      (x) => String(x).toLowerCase() === String(current).toLowerCase()
    );

    return found || current;
  };

  const validateFeatureName = () => {
    const value = String(formData["Feature Name"] || "").trim();

    if (!value) return "Feature name is required.";
    if (value.length < 3) return "Feature name must be at least 3 characters.";
    if (value.length > 100) return "Feature name must be under 100 characters.";

    return "";
  };

  const validateFeatureDescription = () => {
    const value = String(formData["Feature Description"] || "").trim();

    if (value.length > 500) {
      return "Feature description must be under 500 characters.";
    }

    return "";
  };

  const validatePrice = () => {
    const raw = formData["Price"];

    if (raw === "" || raw === null || raw === undefined) {
      return "Price is required.";
    }

    const value = Number(raw);

    if (Number.isNaN(value)) {
      return "Price must be a valid number.";
    }

    if (value < 0) {
      return "Price cannot be negative.";
    }

    if (value > 1000000) {
      return "Price is too large.";
    }

    return "";
  };

  const validateCategory = () => {
    const category = resolveCategory();
    if (!category) return "Feature category is required.";
    return "";
  };

  const validateForm = () => {
    const newErrors = {
      "Feature Name": validateFeatureName(),
      "Feature Description": validateFeatureDescription(),
      "Price": validatePrice(),
      "Feature Category": validateCategory(),
    };

    setErrors(newErrors);

    const firstError = Object.values(newErrors).find(Boolean);
    if (firstError) {
      setSubmitError(firstError);
      return false;
    }

    setSubmitError(null);
    return true;
  };


  const normalizeForCompare = (data) => ({
    "Feature Name": String(data?.["Feature Name"] || "").trim(),
    "Feature Description": String(data?.["Feature Description"] || "").trim(),
    "Price": Number(data?.["Price"] || 0),
    "Core Feature": !!data?.["Core Feature"],
    "Feature Category": String(data?.["Feature Category"] || "").trim(),
    "Feature Dependencies": normalizeDependencyValues(
      data?.["Feature Dependencies"]
    ).sort(),
    "Resource Dependencies": normalizeDependencyValues(
      data?.["Resource Dependencies"]
    ).sort(),
  });

  const hasFeatureChanges = () => {
    if (!isEdit || !initialFormData) return true;

    const current = normalizeForCompare(formData);
    const initial = normalizeForCompare(initialFormData);

    return JSON.stringify(current) !== JSON.stringify(initial);
  };



  const handleSubmit = () => {
    if (!validateForm()) return;

    if (isEdit && initialFormData && !hasFeatureChanges()) {
      setSubmitError("No changes detected.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);

    const { ["Feature Category"]: _, ...rest } = formData;

    const featureDependencies = normalizeDependencyValues(
      formData["Feature Dependencies"]
    );
    const resourceDependencies = normalizeDependencyValues(
      formData["Resource Dependencies"]
    );

    const payload = {
      ...rest,
      Avalability: true,
      "Feature Name": String(formData["Feature Name"] || "").trim(),
      "Feature Description": String(
        formData["Feature Description"] || ""
      ).trim(),
      "Feature Dependencies": featureDependencies,
      Price: Number(formData["Price"]),
      Category: resolveCategory(),
    };

    if (
      schema?.properties?.["Resource Dependencies"] ||
      resourceOptions.length > 0 ||
      Object.prototype.hasOwnProperty.call(formData, "Resource Dependencies")
    ) {
      payload["Resource Dependencies"] = resourceDependencies;
    }

    const apiUrl =
      isEdit && editData?.id
        ? `${ window.ENV.API_URL }/forms/admin/update_feature/${ editData.id }`
        : `${ window.ENV.API_URL }/forms/admin/create_feature`;

  const method = "post";

  getDataFromApi(apiUrl, method, payload)
    .then((response) => {
      if (isEdit) {
        throwIfPlanAssignmentResponse(response);
      }

      setSubmitLoading(false);
      onClose?.();
      onSubmit?.();
    })
    .catch((e) => {
      setSubmitLoading(false);

      const apiMessage = extractApiErrorMessage(e);
      let message = apiMessage || "Failed to submit request. Please try again.";

      if (isEdit && isPlanAssignmentError(apiMessage)) {
        setEditBlocked(true);
        message = apiMessage || getPlanAssignmentEditMessage("feature");
      }

      setSubmitError(message);
    });
};

if (!showModal) return null;

if (formLoading) {
  return (
    <div className="center-form">
      <div >Loading…</div>
    </div>
  );
}

if (!schema) return null;

const isUpdateBlocked = isEdit && editBlocked;
const featureDependencyItems = mergeDependencyItems(
  schema.properties["Feature Dependencies"]?.items,
  featureOptions.filter((feature) => String(feature?.id) !== String(editData?.id))
);
const resourceDependencyItems = mergeDependencyItems(
  schema.properties["Resource Dependencies"]?.items,
  resourceOptions
);
const moduleNameById = moduleOptions.reduce((acc, module) => {
  acc[String(module.id)] = module.name;
  acc[String(module.id).toLowerCase()] = module.name;
  return acc;
}, {});
const resolvedCategory = resolveCategory();
const displayModuleName =
  moduleNameById[String(resolvedCategory)] ||
  moduleNameById[String(resolvedCategory).toLowerCase()] ||
  entitlementName ||
  String(entitlementId || "selected module")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const hasResourceDependencyField =
  Boolean(schema.properties["Resource Dependencies"]) ||
  resourceDependencyItems.enum.length > 0;
const removeDependencyAt = (fieldKey, removeIndex) => {
  const currentValues = Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [];
  update(
    fieldKey,
    currentValues.filter((_, index) => index !== removeIndex)
  );
};


  const fieldTitle = (key, fallback = key) => {
    const title = schema?.properties?.[key]?.title;
    if (typeof title === "string" && title.length) return title;
    return fallback;
  };

return (
  <>
    <div className="center-form">
      <div >
        <div className="cf-head">
          <div>
            {/* <span className="cf-tag text-primary">
              {isEdit ? "Edit feature in " : "Add feature to "}
              {displayModuleName}
            </span> */}

            <h2>
              {isEdit ? "Edit" : "New"}{" "}
              <strong className="text-primary">{displayModuleName}</strong> Feature
            </h2>
          </div>

          <button className="cf-close text-secondary" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="cf-body">
          <label className={errors["Feature Name"] ? "error" : ""}>
            {fieldTitle("Feature Name", "Feature Name")} <span className="req">*</span>
            <input
              value={formData["Feature Name"] || ""}
              autoFocus
              className="text"
              onChange={(e) => {
                update("Feature Name", e.target.value);
                clearFieldError("Feature Name");
              }}
              onBlur={() => {
                setErrors((prev) => ({
                  ...prev,
                  "Feature Name": validateFeatureName(),
                }));
              }}
            />
            {errors["Feature Name"] && (
              <small className="text-danger">{errors["Feature Name"]}</small>
            )}
          </label>

          <label className={errors["Feature Description"] ? "error" : ""}>
            {fieldTitle("Feature Description", "Feature Description")}
            <textarea
              className="text"
              placeholder="Describe the technical requirements and anticipated business impact..."
              value={formData["Feature Description"] || ""}
              onChange={(e) => {
                update("Feature Description", e.target.value);
                clearFieldError("Feature Description");
              }}
              onBlur={() => {
                setErrors((prev) => ({
                  ...prev,
                  "Feature Description": validateFeatureDescription(),
                }));
              }}
            />
            {errors["Feature Description"] && (
              <small className="text-danger">
                {errors["Feature Description"]}
              </small>
            )}
          </label>

          <label className={errors["Feature Dependencies"] ? "error" : ""}>
            {fieldTitle("Feature Dependencies", "Feature Dependencies")}

            <div className="cf-chips text">
              {(Array.isArray(formData["Feature Dependencies"])
                ? formData["Feature Dependencies"].filter(
                  (val) => val && String(val).trim() !== ""
                )
                : []
              ).map((val, selectedIndex) => {
                const allVals = featureDependencyItems.enum;
                const allNames = featureDependencyItems.enumNames;

                const idx = allVals.findIndex((x) => String(x) === String(val));
                const label = idx >= 0 ? allNames[idx] : String(val);

                return (
                  <span key={`${String(val)}-${selectedIndex}`} className="cf-chip">
                    {label}
                    <button
                      type="button"
                      className="cf-chip-remove text-danger"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        removeDependencyAt("Feature Dependencies", selectedIndex);
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>

            <select
              value=""
              className="text"
              onChange={(e) => {
                const selected = e.target.value;
                if (!selected) return;

                const prev = Array.isArray(formData["Feature Dependencies"])
                  ? formData["Feature Dependencies"]
                  : [];

                if (prev.some((x) => String(x) === String(selected))) return;

                update("Feature Dependencies", [...prev, selected]);
                clearFieldError("Feature Dependencies");
                e.target.value = "";
              }}
            >
              <option value="" disabled>
                Select Feature dependencies…
              </option>

              {featureDependencyItems.enum
                .filter((val) => {
                  const prev = Array.isArray(formData["Feature Dependencies"])
                    ? formData["Feature Dependencies"]
                    : [];
                  return !prev.some((x) => String(x) === String(val));
                })
                .map((val) => {
                  const allVals = featureDependencyItems.enum;
                  const allNames = featureDependencyItems.enumNames;
                  const idx = allVals.findIndex((x) => String(x) === String(val));
                  const label = idx >= 0 ? allNames[idx] : String(val);

                  return (
                    <option key={String(val)} value={val}>
                      {label}
                    </option>
                  );
                })}
            </select>

            {errors["Feature Dependencies"] && (
              <small className="text-danger">
                {errors["Feature Dependencies"]}
              </small>
            )}
          </label>



          {hasResourceDependencyField && <label className={errors["Resource Dependencies"] ? "error" : ""}>
            {fieldTitle("Resource Dependencies", "Resource Dependencies")}

            <div className="cf-chips text">
              {(Array.isArray(formData["Resource Dependencies"])
                ? formData["Resource Dependencies"].filter(
                  (val) => val && String(val).trim() !== ""
                )
                : []
              ).map((val, selectedIndex) => {
                const allVals = resourceDependencyItems.enum;
                const allNames = resourceDependencyItems.enumNames;

                const idx = allVals.findIndex((x) => String(x) === String(val));
                const label = idx >= 0 ? allNames[idx] : String(val);

                return (
                  <span key={`${String(val)}-${selectedIndex}`} className="cf-chip">
                    {label}
                    <button
                      type="button"
                      className="cf-chip-remove text-danger"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        removeDependencyAt("Resource Dependencies", selectedIndex);
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>

            <select
              value=""
              className="text"
              onChange={(e) => {
                const selected = e.target.value;
                if (!selected) return;

                const prev = Array.isArray(formData["Resource Dependencies"])
                  ? formData["Resource Dependencies"]
                  : [];

                if (prev.some((x) => String(x) === String(selected))) return;

                update("Resource Dependencies", [...prev, selected]);
                clearFieldError("Resource Dependencies");
                e.target.value = "";
              }}
            >
              <option value="" disabled>
                Select Resource Dependencies...
              </option>

              {resourceDependencyItems.enum
                .filter((val) => {
                  const prev = Array.isArray(formData["Resource Dependencies"])
                    ? formData["Resource Dependencies"]
                    : [];
                  return !prev.some((x) => String(x) === String(val));
                })
                .map((val) => {
                  const allVals = resourceDependencyItems.enum;
                  const allNames = resourceDependencyItems.enumNames;
                  const idx = allVals.findIndex((x) => String(x) === String(val));
                  const label = idx >= 0 ? allNames[idx] : String(val);

                  return (
                    <option key={String(val)} value={val}>
                      {label}
                    </option>
                  );
                })}
            </select>

            {errors["Resource Dependencies"] && (
              <small className="text-danger">
                {errors["Resource Dependencies"]}
              </small>
            )}
          </label>}

          <label className={errors["Price"] ? "error" : ""}>
            PRICE <span className="req">*</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              className="text"
              value={formData["Price"] ?? ""}
              onChange={(e) => {
                update("Price", e.target.value);
                clearFieldError("Price");
              }}
              onBlur={() => {
                if (formData["Price"] === "") {
                  update("Price", "");
                } else {
                  update("Price", Number(formData["Price"]));
                }

                setErrors((prev) => ({
                  ...prev,
                  Price: validatePrice(),
                }));
              }}
            />
            {errors["Price"] && (
              <small className="text-danger">{errors["Price"]}</small>
            )}
          </label>

          <div className="cf-flags">
            <label className="switch">
              <input
                type="checkbox"
                checked={!!formData["Core Feature"]}
                onChange={(e) => {
                  update("Core Feature", e.target.checked);
                }}
              />
              <span />
              Core Feature
            </label>
          </div>
        </div>

        {submitError && (
          <div
            className={
              isUpdateBlocked
                ? "cf-blocked-message"
                : "text-danger text-center w-100 fw-bold mt-2"
            }
            role={isUpdateBlocked ? "alert" : undefined}
            style={isUpdateBlocked ? undefined : { fontSize: "small" }}
          >
            {submitError}
          </div>
        )}

        <div className="cf-footer">
          <div className="cf-note">
            {formData["Core Feature"]
              ? "Bundled Feature. Turn off Core for à la carte."
              : "À la carte add-on. Turn on Core to bundle."}
          </div>

          <button
            className="cf-submit bg-primary"
            onClick={handleSubmit}
            disabled={submitLoading || isUpdateBlocked}
          >
            {submitLoading
              ? isEdit
                ? "Updating…"
                : "Submitting…"
              : isUpdateBlocked
                ? "Cannot Update"
                : isEdit
                ? "Update Feature"
                : "Submit Request"}
          </button>
        </div>
      </div>
    </div>

    <div className="mobile-view-bottom"></div>
  </>
);
}
